import React, { useState } from "react";
import { Form, InputGroup, Button, Row, Col } from "react-bootstrap";
import { useSyncedField } from "../../components/Sidebar";
import IntegrationCollapsible from "./IntegrationCollapsible";

// Integration.config shape: { values: { [field.key]: value, ... } }
// Field shape (from the integrations.available catalog template):
//   { key, label, type, required, secret?, placeholder?, help_text?, group?, options?, show_if? }

const GROUP_TITLES = {
    connection: "Connection",
    auth: "Authentication",
    advanced: "Advanced",
};

function isFieldVisible(field, values) {
    if (!field.show_if) return true;
    return values[field.show_if.key] === field.show_if.equals;
}

function KeyValueField({ label, value, onChange }) {
    const rows = Object.entries(value || {}).map(([key, val]) => ({ key, value: String(val) }));

    const commit = (nextRows) => {
        const obj = {};
        for (const { key, value: val } of nextRows) {
            if (key.trim()) obj[key.trim()] = val;
        }
        onChange(obj);
    };

    const addRow = () => commit([...rows, { key: "", value: "" }]);
    const removeRow = (i) => commit(rows.filter((_, idx) => idx !== i));
    const updateKey = (i, key) => commit(rows.map((r, idx) => (idx === i ? { ...r, key } : r)));
    const updateValue = (i, val) => commit(rows.map((r, idx) => (idx === i ? { ...r, value: val } : r)));

    return (
        <Form.Group className="mb-3">
            <Form.Label>{label}</Form.Label>
            {rows.map((row, index) => (
                <Row key={index} className="mb-2 align-items-center g-2">
                    <Col xs={5}>
                        <Form.Control
                            size="sm"
                            value={row.key}
                            onChange={(e) => updateKey(index, e.target.value)}
                            placeholder="Name"
                        />
                    </Col>
                    <Col xs={6}>
                        <Form.Control
                            size="sm"
                            value={row.value}
                            onChange={(e) => updateValue(index, e.target.value)}
                            placeholder="Value"
                        />
                    </Col>
                    <Col xs={1}>
                        <Button variant="outline-danger" size="sm" onClick={() => removeRow(index)} title="Remove">
                            <i className="fa fa-times" />
                        </Button>
                    </Col>
                </Row>
            ))}
            <Button variant="outline-primary" size="sm" onClick={addRow} className="mt-1">
                <i className="fa fa-plus me-1" />
                Add
            </Button>
        </Form.Group>
    );
}

function SecretField({ field, value, onChange }) {
    const [show, setShow] = useState(false);
    return (
        <Form.Group className="mb-3">
            <Form.Label>
                {field.label} {field.required && <span className="text-danger">*</span>}
            </Form.Label>
            <InputGroup>
                <Form.Control
                    type={show ? "text" : "password"}
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={field.placeholder}
                />
                <Button variant="outline-secondary" onClick={() => setShow((v) => !v)} tabIndex={-1}>
                    <i className={`fa fa-eye${show ? "-slash" : ""}`} />
                </Button>
            </InputGroup>
            {field.help_text && <Form.Text className="text-muted">{field.help_text}</Form.Text>}
        </Form.Group>
    );
}

function ConfigField({ field, value, onChange }) {
    if (field.type === "keyvalue") {
        return <KeyValueField label={field.label} value={value} onChange={onChange} />;
    }

    if (field.type === "password") {
        return <SecretField field={field} value={value} onChange={onChange} />;
    }

    if (field.type === "boolean") {
        return (
            <Form.Group className="mb-3">
                <Form.Check
                    type="switch"
                    id={`integration-field-${field.key}`}
                    label={field.label}
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                />
                {field.help_text && <Form.Text className="text-muted d-block">{field.help_text}</Form.Text>}
            </Form.Group>
        );
    }

    if (field.type === "select") {
        return (
            <Form.Group className="mb-3">
                <Form.Label>
                    {field.label} {field.required && <span className="text-danger">*</span>}
                </Form.Label>
                <Form.Select value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
                    <option value="" disabled>
                        Select…
                    </option>
                    {(field.options || []).map((opt) => (
                        <option key={opt} value={opt}>
                            {opt}
                        </option>
                    ))}
                </Form.Select>
                {field.help_text && <Form.Text className="text-muted">{field.help_text}</Form.Text>}
            </Form.Group>
        );
    }

    if (field.type === "textarea") {
        return (
            <Form.Group className="mb-3">
                <Form.Label>
                    {field.label} {field.required && <span className="text-danger">*</span>}
                </Form.Label>
                <Form.Control
                    as="textarea"
                    rows={4}
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={field.placeholder}
                    className="font-monospace font-size-13px"
                />
                {field.help_text && <Form.Text className="text-muted">{field.help_text}</Form.Text>}
            </Form.Group>
        );
    }

    // text | url (and fallback)
    return (
        <Form.Group className="mb-3">
            <Form.Label>
                {field.label} {field.required && <span className="text-danger">*</span>}
            </Form.Label>
            <Form.Control
                type={field.type === "url" ? "url" : "text"}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={field.placeholder}
            />
            {field.help_text && <Form.Text className="text-muted">{field.help_text}</Form.Text>}
        </Form.Group>
    );
}

/**
 * Renders one form driven entirely by a field schema — the same generic
 * engine handles a template's own config_schema (connection/advanced fields)
 * and an auth_schemes.available scheme's fields (via the `authScheme` prop).
 *
 * `groups` is the ordered list of config_schema field groups this instance
 * should render (e.g. ["connection", "advanced"] on the Connection tab) —
 * each tab owns its own groups instead of one form rendering all three
 * together. The first group in the list renders as plain fields (it's
 * already the whole point of the tab); any subsequent groups render inside
 * a collapsible section, for optional/secondary fields like "Advanced" proxy
 * settings. Ignored when `authScheme` is set.
 *
 * `authScheme` (optional): when set, this instance renders an auth scheme's
 * fields instead of a template's config_schema group, reading/writing
 * `config.values.auth[authScheme]` instead of flat `config.values` — this is
 * what namespaces auth field values per-scheme, so switching schemes on the
 * Authentication tab never loses or collides with another scheme's values.
 * Fields render flatly (no groups/collapsible) since a scheme's fields are
 * already scoped to one picker selection.
 */
const IntegrationConfigForm = ({ schema, groups, authScheme }) => {
    const [config, setConfig] = useSyncedField("config", { values: {} });
    const values = config?.values ?? {};
    const scopedValues = authScheme ? (values.auth?.[authScheme] ?? {}) : values;

    const setFieldValue = (key, fieldValue) => {
        if (authScheme) {
            setConfig({
                ...config,
                values: {
                    ...values,
                    auth: {
                        ...(values.auth ?? {}),
                        [authScheme]: { ...scopedValues, [key]: fieldValue },
                    },
                },
            });
        } else {
            setConfig({ ...config, values: { ...values, [key]: fieldValue } });
        }
    };

    const fields = (schema || []).filter((field) => isFieldVisible(field, scopedValues));

    if (authScheme) {
        return (
            <div className="p-3">
                <Form>
                    {fields.map((field) => (
                        <ConfigField
                            key={field.key}
                            field={field}
                            value={scopedValues[field.key]}
                            onChange={(v) => setFieldValue(field.key, v)}
                        />
                    ))}
                </Form>
            </div>
        );
    }

    return (
        <div className="p-3">
            {groups.map((group, index) => {
                const groupFields = fields.filter((f) => (f.group || "connection") === group);
                if (groupFields.length === 0) return null;

                const content = groupFields.map((field) => (
                    <ConfigField
                        key={field.key}
                        field={field}
                        value={scopedValues[field.key]}
                        onChange={(v) => setFieldValue(field.key, v)}
                    />
                ));

                if (index === 0) {
                    return (
                        <Form key={group} className="mb-2">
                            {content}
                        </Form>
                    );
                }

                return (
                    <IntegrationCollapsible key={group} title={GROUP_TITLES[group]}>
                        <Form>{content}</Form>
                    </IntegrationCollapsible>
                );
            })}
        </div>
    );
};

export default IntegrationConfigForm;
